import React from 'react';
import cx from 'classnames';
import _uniqueId from 'lodash/uniqueId';

import COLORS, { COLORS_BACKGROUND } from '../../styles/emotion/colors';
import { LinkV2 } from '../LinkV2';
import { ButtonV3 } from '../ButtonV3';
import { ChevronButton } from '../ChevronButton';
// @ts-expect-error Allow JS import
import scrollViewTo from '../../util/scrollViewTo';
import { DrawerContainer, DrawerContent, DrawerExpandButtonContainer } from './drawerV2.styles';

type DrawerV2Props = {
  className?: string;
  backgroundFadeColor?: 'darkGray' | 'gray' | null;
  /**
   *  Default expanding state. This value will not change it after the component is rendered
   */
  expandedByDefault?: boolean;
  /** Max height when drawer is closed */
  maxHeight: string;
  /**  Max height when drawer is expanded */
  maxExpandedHeight?: string;
  /**  Offset for scrolling function if skipScrollToTop is false */
  offset?: number;
  /**  Skip scrolling to the top when the section is closed */
  skipScrollToTop?: boolean;
  /**  Replace default Chevron Button with custom text or components */
  buttonText?: {
    open?: string | React.ReactNode;
    close?: string | React.ReactNode;
    appearance?: 'button' | 'link';
  };
  /** Disable white fade on bottom when closed */
  disableFadeBottom?: boolean;
};

const DrawerV2: React.FC<DrawerV2Props> = ({
  backgroundFadeColor = null,
  buttonText = {
    open: null,
    close: null,
    appearance: 'link',
  },
  children,
  className,
  disableFadeBottom = false,
  expandedByDefault = false,
  maxHeight,
  maxExpandedHeight = '3000px',
  offset = 0,
  skipScrollToTop,
}) => {
  const [sectionExpanded, setSectionExpanded] = React.useState(expandedByDefault);
  const topOfFAQs = React.useRef(null);
  const drawerContainerId = `drawer__container${_uniqueId()}`;

  const toggleSection = () => {
    setSectionExpanded(() => {
      if (sectionExpanded && !skipScrollToTop) {
        // An id is not necessary when el is provided
        scrollViewTo({ el: topOfFAQs.current, offset });
      }
      return !sectionExpanded;
    });
  };

  const Buttons = () => {
    if (buttonText.open && buttonText.close) {
      return (
        <div>
          {buttonText.appearance === 'button' ? (
            <ButtonV3 variant="secondary" className="drawer__button" onClick={toggleSection}>
              {sectionExpanded ? buttonText.close : buttonText.open}
            </ButtonV3>
          ) : (
            <LinkV2 className="drawer__button-link" onClick={toggleSection} role="button">
              {sectionExpanded ? buttonText.close : buttonText.open}
            </LinkV2>
          )}
        </div>
      );
    }
    return (
      <ChevronButton
        btnStyle="secondary"
        direction={sectionExpanded ? 'up' : 'down'}
        onClick={toggleSection}
        ariaLabel={sectionExpanded ? 'close section' : 'expand section'}
        aria-controls={drawerContainerId}
        aria-expanded={sectionExpanded}
      />
    );
  };
  const getBackgroundFadeColorClass = (backgroundFadeColor: string | null): string | undefined => {
    if (backgroundFadeColor === 'darkGray') {
      return COLORS.PRIVILEGE;
    }
    if (backgroundFadeColor === 'gray') {
      return COLORS_BACKGROUND.GRAY_2;
    }
    return undefined;
  };

  const currentHeight = sectionExpanded ? maxExpandedHeight : maxHeight;
  const fadeBottom = !(sectionExpanded || disableFadeBottom);

  return (
    <DrawerContainer
      ref={topOfFAQs}
      className={cx('drawer', className, { 'fade-bottom': fadeBottom })}
      fadeBottom={fadeBottom}
    >
      <DrawerContent
        id={drawerContainerId}
        style={{ maxHeight: currentHeight }}
        className="drawer__content"
        fadeBottom={fadeBottom}
        backgroundFadeColor={getBackgroundFadeColorClass(backgroundFadeColor)}
      >
        {children}
      </DrawerContent>
      <DrawerExpandButtonContainer className="drawer__expand-button-container">
        <Buttons />
      </DrawerExpandButtonContainer>
    </DrawerContainer>
  );
};

export default DrawerV2;
